import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./Renew.css";

const PoliceSeizeForm = () => {
  const navigate = useNavigate();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState("");
  const [confirmSeized, setConfirmSeized] = useState(null);
  const [reconfirmSeized, setReconfirmSeized] = useState(null);
  const [showDivisionInput, setShowDivisionInput] = useState(false);
  const [policeDivision, setPoliceDivision] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);
  const divisionRef = useRef(null);

  // Autofocus MMYY on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      mmyyRef.current?.focus();
      mmyyRef.current?.select?.();
    });
  }, []);

  // Focus Division input when it appears
  useEffect(() => {
    if (showDivisionInput) {
      requestAnimationFrame(() => {
        divisionRef.current?.focus();
      });
    }
  }, [showDivisionInput]);

  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
      receiptRef.current?.select?.();
    }
  };

  const onReceiptKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (mmyy && receiptNo) {
        handleSearch();
      }
    }
  };

  const onDivisionKeyDown = (e) => {
    if (e.key === "Enter" && policeDivision.trim() && !submitting) {
      e.preventDefault();
      handleFinalSubmit();
    }
  };

  const handleInitialConfirm = (choice) => {
    if (choice === "yes") {
      setConfirmSeized(true);
    } else {
      setConfirmSeized(false);
      setReconfirmSeized(null);
      setShowDivisionInput(false);
    }
  };

  const handleReConfirm = (choice) => {
    if (choice === "yes") {
      setReconfirmSeized(true);
      setShowDivisionInput(true);
    } else {
      setReconfirmSeized(false);
      setShowDivisionInput(false);
    }
  };

  const handleSearch = async () => {
    try {
      const response = await axios.get(
        "https://www.lianfoong.com/api/pawn-transactions/search",
        {
          params: { mmyy, receiptNo },
        }
      );

      setTransaction(response.data);
      setError("");
      setConfirmSeized(null);
      setReconfirmSeized(null);
      setShowDivisionInput(false);
      setPoliceDivision("");
    } catch (err) {
      console.error(err);
      setError("Transaction not found!");
      setTransaction(null);
      setConfirmSeized(null);
      setReconfirmSeized(null);
      setShowDivisionInput(false);
      setPoliceDivision("");
    }
  };

  const handleFinalSubmit = async () => {
    if (!transaction?.receiptNo) {
      alert("Please search and select a valid ticket first.");
      return;
    }

    if (!policeDivision.trim()) {
      alert("Please enter the police division.");
      return;
    }

    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const localDate = `${year}-${month}-${day}`;

    // use the receipt returned by backend search, not raw typed value
    const finalReceiptNo = transaction.receiptNo;

    const seizedData = {
      receiptNo: finalReceiptNo,
      seizedDate: localDate,
      seizedBy: policeDivision.trim(),
      claimed: false,
    };

    try {
      setSubmitting(true);

      // 1) Save police seized detail
      await axios.post(
        "https://www.lianfoong.com/api/police-size-details",
        seizedData
      );

      // 2) Deactivate the ticket
      await axios.put(
        "https://www.lianfoong.com/api/pawn-transactions/deactivate-old-ticket",
        null,
        {
          params: { oldReceiptNo: finalReceiptNo },
        }
      );

      alert("Police seizure recorded and ticket deactivated.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Submission failed:", error);
      alert(
        error?.response?.data || "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateDDMMYYYY = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="renew-container">
      <h2 className="title">Police Seized Items</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          type="text"
          ref={mmyyRef}
          value={mmyy}
          maxLength={4}
          onChange={(e) => setMmyy(e.target.value.replace(/\D/g, "").slice(0, 4))}
          onKeyDown={onMmyyKeyDown}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          type="text"
          ref={receiptRef}
          value={receiptNo}
          maxLength={5}
          onChange={(e) => setReceiptNo(e.target.value.replace(/\D/g, ""))}
          onKeyDown={onReceiptKeyDown}
        />
      </div>

      <button className="search-btn" onClick={handleSearch} disabled={!mmyy || !receiptNo}>
        Search
      </button>

      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Receipt No:</strong> {transaction.receiptNo}</p>
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${Number(transaction.amount || 0).toFixed(2)}</p>
            <p><strong>Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
          </div>

          {confirmSeized === null && (
            <div className="alert-box">
              <p><strong>Please confirm the item is seized by the police:</strong></p>
              <button onClick={() => handleInitialConfirm("yes")}>Yes</button>
              <button onClick={() => handleInitialConfirm("no")}>No</button>
            </div>
          )}

          {confirmSeized === true && reconfirmSeized === null && (
            <div className="alert-box">
              <p><strong>Please Re-confirm:</strong></p>
              <button onClick={() => handleReConfirm("yes")}>Yes</button>
              <button onClick={() => handleReConfirm("no")}>No</button>
            </div>
          )}

          {showDivisionInput && (
            <div className="form-group">
              <label>Which police division was it seized by?</label>
              <input
                type="text"
                ref={divisionRef}
                value={policeDivision}
                onChange={(e) => setPoliceDivision(e.target.value)}
                onKeyDown={onDivisionKeyDown}
              />
              <button
                className="next-btn"
                onClick={handleFinalSubmit}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PoliceSeizeForm;